import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches render-time exceptions so one component's failure shows a recoverable
 * card instead of unmounting the whole SPA to a blank white page.
 *
 * Why this exists: React unmounts the entire tree on an uncaught render
 * exception, so a single bad value anywhere becomes a blank page with no error,
 * no toast, and nothing in the UI to act on — the operator sees white and files
 * "the page is broken". The console hit this twice in 2026-06 (nil Go slices
 * serializing to `null`, dereferenced by the SPA) and added a boundary; MC had
 * none, so the ADR-123 delete panel's nil-slice crash took the whole app down
 * rather than one panel. The underlying bug is fixed separately — this is the
 * layer that makes the NEXT one survivable and diagnosable.
 *
 * Deliberately not wrapped per-route: the point is to catch what we did not
 * anticipate, and a boundary only helps where it is mounted.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The component stack is what turns "white page" into a file and a line.
    console.error('Mission Control UI crashed:', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-base font-semibold text-gray-900 mb-1">Something went wrong</p>
            <p className="text-sm text-gray-500 mb-4">
              Mission Control hit an unexpected error while rendering. Reloading usually clears it.
              Nothing you were doing was submitted.
            </p>
            <p className="text-xs font-mono text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4 break-words text-left">
              {this.state.error.message}
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={() => { window.location.href = '/' }}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Back to start
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
